import React, { useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import usePoll from "../../hooks/usePoll";
import { useSocketEvent } from "../../hooks/useSocketEvent";
import { chime } from "../../utils/chime";
import { TagIcon } from "../../utils/tagIcons";
import { showToast } from "../errors";
import { useNow } from "../osr/useCountdown";
import { failureText, webChatsApi } from "./webChatsApi";
import { timeLeft } from "./WebChatLive";
import "../osr/OsrAlerts.css";

const POLL_MS = 60000;


/**
 * Website chat, Stage 2 — "someone wants a person" (docs/WEB_CHAT.md; chariot-backend services/webChat/live.js). Cards
 * at the bottom right of every page, for people who can take live chats (web_chat.take):
 *   a visitor asked Hank for a person: who, which team, what about, the time left to accept, with a chime; the first to
 *     Accept gets the chat (everyone else's card goes)
 *   my live chats: a card back to each one, from anywhere in Chariot (not on that chat's own page)
 * The hand-offs come by socket (web_chat:handoff, sent only to people taking chats who have Chariot open); any change
 * (web_chat:live) reads the list again, and so does every page load.
 *
 * Rendered inside the sitewide alert stack (SiteAlertCards) in the OSR card's look (components/osr/OsrAlerts.css).
 *
 *   POLL BUDGET  once a minute per open tab, only for people who can take chats, paused while the tab is hidden
 *   (usePoll) — a backstop for a socket notice that was missed.
 */
export default function LiveChatAlerts() {
  const { can, user } = useAuth();
  const allowed = !!user && can("web_chat.take");
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState({ taking: false, waiting: [], mine: [] });
  const [busy, setBusy] = useState(null);
  const [dismissed, setDismissed] = useState(() => new Set());
  const heard = useRef(new Set()); // the hand-offs already chimed for

  const load = useCallback(async () => {
    try {
      setData(await webChatsApi.live());
      return true;
    } catch {
      return false; // usePoll backs off; the last cards stay up
    }
  }, []);
  const readNow = usePoll(load, POLL_MS, { enabled: allowed });

  const onHandoff = useCallback((card) => {
    if (!card?.chatId) return;
    setData((d) => ({ ...d, waiting: [...(d.waiting || []).filter((c) => c.chatId !== card.chatId), card] }));
    if (!heard.current.has(card.chatId)) {
      heard.current.add(card.chatId);
      chime();
    }
  }, []);
  useSocketEvent("web_chat:handoff", allowed ? onHandoff : null);
  const onLive = useCallback(() => { readNow(); }, [readNow]);
  useSocketEvent("web_chat:live", allowed ? onLive : null);

  const waiting = allowed ? (data.waiting || []).filter((c) => !dismissed.has(c.chatId)) : [];
  const here = new URLSearchParams(location.search).get("chat");
  const mine = allowed ? (data.mine || []).filter((c) => c.chatId !== here) : [];
  const now = useNow(1000, waiting.length > 0);
  if (!waiting.length && !mine.length) return null;

  const accept = async (card) => {
    setBusy(card.chatId);
    try {
      await webChatsApi.accept(card.chatId);
      navigate(`/app${card.link}`);
    } catch (err) {
      showToast.error(failureText(err));
    } finally {
      setBusy(null);
      readNow();
    }
  };

  return (
    <>
      {waiting.map((c) => (
        <div key={c.chatId} className="osr-alert" data-level="high" role="alert">
          <span className="osr-alert-icon" aria-hidden="true"><TagIcon name="message" size={17} /></span>
          <div className="osr-alert-body">
            <div className="osr-alert-title">Website chat: someone wants a person</div>
            <div className="osr-alert-name">{c.name}</div>
            <div className="osr-alert-meta">
              <span>{[c.team_label, c.about].filter(Boolean).join(" · ") || "Website chat"}</span>
              <span>{timeLeft(c.deadline_at, now) ? `${timeLeft(c.deadline_at, now)} to accept` : "Time is up any moment now"}, then Hank takes it back and saves the request</span>
            </div>
            <div className="osr-alert-actions">
              <button type="button" className="osr-alert-btn osr-alert-btn-main" onClick={() => accept(c)} disabled={busy === c.chatId}>
                {busy === c.chatId ? "Joining…" : "Accept"}
              </button>
              <button type="button" className="osr-alert-btn" onClick={() => setDismissed((d) => new Set(d).add(c.chatId))}>Not now</button>
            </div>
          </div>
        </div>
      ))}
      {mine.map((c) => (
        <div key={c.chatId} className="osr-alert">
          <span className="osr-alert-icon" aria-hidden="true"><TagIcon name="message" size={17} /></span>
          <div className="osr-alert-body">
            <div className="osr-alert-title">You're chatting with a website visitor</div>
            <div className="osr-alert-name">{c.name}</div>
            <div className="osr-alert-actions">
              <button type="button" className="osr-alert-btn osr-alert-btn-main" onClick={() => navigate(`/app${c.link}`)}>Open the chat</button>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
